import React from 'react';
import { Table, ConfigProvider, Empty, Space, Button, Typography } from 'antd';
import { CalendarOutlined, CloudDownloadOutlined, FilePdfOutlined, InfoCircleOutlined } from '@ant-design/icons';

import { noWrapColumn } from '~components/noWrapColumn';
import { CustomTooltip } from '~components/Tooltip';
import { dates } from '~library/dates';
import InvoiceStatus from './InvoiceStatus';
import { useIsMobileView } from '../../../hooks/useIsMobileView';
import { formatCentsToDollar } from '../../../assets/currencies';
import { createUsNumberFormat } from '../../../utils/createUsNumberFormat';

function BillingHistoryTable({ isLoading = false, items = [], handleOpenInvoicePdf, hideRendersColumns = false }) {
  const isMobile = useIsMobileView();

  const visibleColumns = React.useMemo(() => {
    const columns = [
      {
        title: 'Invoice',
        width: '99%',
        ...noWrapColumn,
        render: (_text, item) => (
          <Space>
            {!isMobile && (item.status === 'payment_due' ? <CalendarOutlined /> : <FilePdfOutlined />)}
            <Typography.Text>#{item.id}</Typography.Text>
          </Space>
        ),
      },
      {
        title: 'Issue date',
        ...noWrapColumn,
        render: (_text, item) => dates.calendarDay(item.date),
      },
      {
        title: 'Due date',
        ...noWrapColumn,
        render: (_text, item) => dates.calendarDay(item.dueDate),
        isHidden: isMobile,
      },
      {
        title: 'Amount',
        dataIndex: 'totalInCent',
        render: (text) => formatCentsToDollar(text),
      },
      {
        title: (
          <Space size={4}>
            <span>Charged Amount</span>
            <CustomTooltip
              placement="top"
              message="For plan changes, this can be a prorated charge for the current billing period."
            >
              <InfoCircleOutlined />
            </CustomTooltip>
          </Space>
        ),
        ...noWrapColumn,
        dataIndex: 'amountPaidInCent',
        render: (text) => (typeof text === 'number' ? formatCentsToDollar(text) : '-'),
      },
      {
        title: 'Included renders',
        ...noWrapColumn,
        dataIndex: 'includedRenders',
        render: (text) => createUsNumberFormat(text || 0),
        isHidden: hideRendersColumns,
      },
      {
        title: 'Overage billed as',
        ...noWrapColumn,
        dataIndex: 'overageBilledAsPlanName',
        render: (text) => text || '-',
        isHidden: hideRendersColumns,
      },
      {
        title: 'Extra renders',
        ...noWrapColumn,
        dataIndex: 'extraRenders',
        render: (text) => createUsNumberFormat(text || 0),
        isHidden: hideRendersColumns,
      },
      {
        title: 'Total renders',
        ...noWrapColumn,
        dataIndex: 'renders',
        render: (text) => createUsNumberFormat(text || 0),
        isHidden: hideRendersColumns,
      },
      {
        title: 'Payment date',
        ...noWrapColumn,
        render: (_text, item) => dates.dayOfPointInTime(item.paidAt),
        isHidden: isMobile,
      },
      {
        title: 'Status',
        render: (_text, item) => (
          <Space>
            <InvoiceStatus invoice={item} />
            <Button icon={<CloudDownloadOutlined />} onClick={() => handleOpenInvoicePdf(item.id)}></Button>
          </Space>
        ),
      },
    ];

    return columns.filter((column) => !column.isHidden);
  }, [isMobile, handleOpenInvoicePdf, hideRendersColumns]);

  return (
    <ConfigProvider
      renderEmpty={() => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="You don't have any invoices yet." />}
    >
      <Table
        bordered={!isLoading}
        rowKey="id"
        loading={isLoading}
        columns={visibleColumns}
        dataSource={items}
        pagination={false}
        showHeader={!!items.length}
        scroll={{ x: true }}
      />
    </ConfigProvider>
  );
}

export default BillingHistoryTable;
